import { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Skeleton,
  Stack,
} from "@mui/material";
import { PageLayout } from "common-fe";
import { useActiveOrg } from "common-fe";
import { useTranslation } from "i18n-fe";
import { useSuperOrgs, PRODUCT_APPS } from "../hooks/useSuperOrgs";

// Source labels — kept readable; unknown/legacy orgs show a neutral dash.
const sourceLabel = (source) => source || "—";

export default function SuperOrganizations() {
  const { t } = useTranslation("common");
  const { isSuper } = useActiveOrg();
  const { orgs, loading, error, provision, setEntitlements, setSuperadminEnabled } =
    useSuperOrgs();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", ownerEmail: "", apps: [] });
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rowError, setRowError] = useState(null);

  if (!isSuper) {
    return (
      <PageLayout maxWidth={1000}>
        <Alert severity="error">{t("superOrgs.notAuthorized")}</Alert>
      </PageLayout>
    );
  }

  const toggleApp = (app) =>
    setForm((f) => ({
      ...f,
      apps: f.apps.includes(app)
        ? f.apps.filter((a) => a !== app)
        : [...f.apps, app],
    }));

  const submitProvision = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await provision({
        name: form.name.trim(),
        ownerEmail: form.ownerEmail.trim(),
        entitlements: form.apps,
      });
      setDialogOpen(false);
      setForm({ name: "", ownerEmail: "", apps: [] });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEntitlement = async (org, app, checked) => {
    setRowError(null);
    const current = Array.isArray(org.entitlements) ? org.entitlements : [];
    const next = checked
      ? Array.from(new Set([...current, app]))
      : current.filter((a) => a !== app);
    try {
      await setEntitlements(org.id, next);
    } catch (err) {
      setRowError(err.message);
    }
  };

  const toggleSuperadminEnabled = async (org, checked) => {
    setRowError(null);
    try {
      await setSuperadminEnabled(org.id, checked);
    } catch (err) {
      setRowError(err.message);
    }
  };

  return (
    <PageLayout maxWidth={1000}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ color: "text.primary" }}>
          {t("superOrgs.heading")}
        </Typography>
        <Button
          variant="contained"
          data-testid="provision-org-button"
          onClick={() => setDialogOpen(true)}
        >
          {t("superOrgs.newOrg")}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {rowError && <Alert severity="error" sx={{ mb: 2 }}>{rowError}</Alert>}

      {loading ? (
        <Box>
          <Skeleton height={48} />
          <Skeleton height={48} />
          <Skeleton height={48} />
        </Box>
      ) : (
        <Table size="small" data-testid="orgs-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("superOrgs.colName")}</TableCell>
              <TableCell>{t("superOrgs.colOwner")}</TableCell>
              <TableCell>{t("superOrgs.colApps")}</TableCell>
              <TableCell>{t("superOrgs.colSuperadminEnabled")}</TableCell>
              <TableCell>{t("superOrgs.colSource")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orgs.map((org) => (
              <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                <TableCell sx={{ color: "text.primary" }}>{org.name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {org.owner?.email || org.owner?.userId || "—"}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={2}>
                    {PRODUCT_APPS.map((app) => {
                      const entitled = (org.entitlements || []).includes(app);
                      return (
                        <FormControlLabel
                          key={app}
                          control={
                            <Switch
                              size="small"
                              checked={entitled}
                              data-testid={`entitle-${org.id}-${app}`}
                              onChange={(e) =>
                                toggleEntitlement(org, app, e.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: "text.primary" }}>
                              {app}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    checked={org.superadminEnabled === true}
                    data-testid={`superadmin-enabled-${org.id}`}
                    title={t("superOrgs.superadminEnabledHelp")}
                    onChange={(e) =>
                      toggleSuperadminEnabled(org, e.target.checked)
                    }
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={sourceLabel(org.source)}
                    variant="outlined"
                    sx={{ color: "text.primary", borderColor: "divider" }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("superOrgs.provisionTitle")}</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            fullWidth
            margin="normal"
            label={t("superOrgs.orgNameLabel")}
            value={form.name}
            data-testid="provision-name"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t("superOrgs.ownerEmailLabel")}
            value={form.ownerEmail}
            data-testid="provision-owner"
            onChange={(e) => setForm((f) => ({ ...f, ownerEmail: e.target.value }))}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, color: "text.primary" }}>
            {t("superOrgs.entitledApps")}
          </Typography>
          <FormGroup row>
            {PRODUCT_APPS.map((app) => (
              <FormControlLabel
                key={app}
                control={
                  <Checkbox
                    checked={form.apps.includes(app)}
                    data-testid={`provision-app-${app}`}
                    onChange={() => toggleApp(app)}
                  />
                }
                label={app}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("superOrgs.cancel")}</Button>
          <Button
            variant="contained"
            disabled={submitting || !form.name.trim() || !form.ownerEmail.trim()}
            data-testid="provision-submit"
            onClick={submitProvision}
          >
            {t("superOrgs.provision")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
